import React, { useEffect, useMemo, useState } from "react";
import { FaEdit, FaTrash, FaPlus, FaFileExcel, FaSearch } from "react-icons/fa";
import AddEditGroupMaster from "./AddEditGroupMaster";
import { axiosMaster } from "../api/axiosInstance";

const GroupMaster = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [editingData, setEditingData] = useState(null);

  /* ================= FETCH ================= */
  const fetchGroups = async () => {
    try {
      setLoading(true);
      const res = await axiosMaster.get("/GroupMaster");
      setData(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  /* ================= SEARCH ================= */
  const filteredData = useMemo(() => {
    return data.filter((item) =>
      item.groupName?.toLowerCase().includes(search.toLowerCase())
    );
  }, [data, search]);

  /* ================= ADD / EDIT ================= */
  const openAdd = () => {
    setEditingData(null);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingData(row);
    setFormOpen(true);
  };

  /* ================= DELETE ================= */
  const handleDelete = async (row) => {
    if (!window.confirm("Delete this group?")) return;
    try {
      await axiosMaster.post("/GroupMaster/Upsert", {
        ...row,
        status: false,
      });
      fetchGroups();
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= EXCEL ================= */
  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      GroupId: r.groupId,
      GroupName: r.groupName,
      Description: r.description,
      Status: r.status ? "Active" : "Inactive",
      EntryDate: r.entryDate,
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((e) => Object.values(e).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = "GroupMaster.csv";
    link.click();
  };

  return (
  <div className="bg-white rounded-xl shadow-lg p-4 md:p-5 h-[calc(100vh-120px)] flex flex-col border border-slate-200">

    {/* HEADER */}
    <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-3">

      <h2 className="text-lg md:text-xl font-bold text-slate-800">
        Group Master
      </h2>

      <div className="flex flex-wrap gap-2">

        {/* SEARCH */}
        <div className="relative w-full sm:w-auto">
          <FaSearch className="absolute left-3 top-3 text-slate-400 text-sm" />
          <input
            type="text"
            placeholder="Search group..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-56 pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {/* EXCEL */}
        <button
          onClick={exportExcel}
          className="flex items-center justify-center gap-2 bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 text-sm w-full sm:w-auto"
        >
          <FaFileExcel />
          Excel
        </button>

        {/* ADD */}
        <button
          onClick={openAdd}
          className="flex items-center justify-center gap-2 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 text-sm w-full sm:w-auto"
        >
          <FaPlus />
          Add Group
        </button>
      </div>
    </div>

    {/* ================= DESKTOP TABLE ================= */}
    <div className="hidden md:block flex-1 overflow-auto custom-scroll border border-slate-300 rounded-lg">

      <table className="w-full text-sm border-collapse">
        <thead className="bg-slate-100 sticky top-0 z-10">
          <tr>
            <th className="border p-2 text-left">Group Id</th>
            <th className="border p-2 text-left">Group Name</th>
            <th className="border p-2 text-left">Description</th>
            <th className="border p-2 text-left">Status</th>
            <th className="border p-2 text-center">Action</th>
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <td colSpan="5" className="text-center p-6 border">
                Loading...
              </td>
            </tr>
          ) : filteredData.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center p-6 border">
                No Data Found
              </td>
            </tr>
          ) : (
            filteredData.map((row) => (
              <tr key={row.groupId} className="hover:bg-slate-50">
                <td className="border p-2">{row.groupId}</td>
                <td className="border p-2 font-medium">{row.groupName}</td>
                <td className="border p-2">{row.description}</td>
                <td className="border p-2">
                  {row.status ? "Active" : "Inactive"}
                </td>
                <td className="border p-2 text-center">
                  <div className="flex justify-center gap-3">
                    <FaEdit
                      onClick={() => openEdit(row)}
                      className="cursor-pointer text-blue-600"
                    />
                    <FaTrash
                      onClick={() => handleDelete(row)}
                      className="cursor-pointer text-red-600"
                    />
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>

    {/* ================= MOBILE CARD VIEW ================= */}
    <div className="md:hidden flex-1 overflow-auto custom-scroll space-y-3">

      {loading ? (
        <div className="text-center py-6">Loading...</div>
      ) : filteredData.length === 0 ? (
        <div className="text-center py-6">No Data Found</div>
      ) : (
        filteredData.map((row) => (
          <div
            key={row.groupId}
            className="border border-slate-200 rounded-xl p-4 shadow-sm"
          >
            <div className="flex justify-between items-center mb-2">
              <h3 className="font-semibold text-slate-800">
                {row.groupName}
              </h3>

              <span
                className={`text-xs px-2 py-1 rounded-full ${
                  row.status
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {row.status ? "Active" : "Inactive"}
              </span>
            </div>

            <p className="text-sm text-slate-600 mb-2">
              <span className="font-medium">ID:</span> {row.groupId}
            </p>

            <p className="text-sm text-slate-600 mb-3">
              <span className="font-medium">Description:</span>{" "}
              {row.description || "-"}
            </p>

            <div className="flex justify-end gap-4">
              <FaEdit
                onClick={() => openEdit(row)}
                className="cursor-pointer text-blue-600 text-lg"
              />
              <FaTrash
                onClick={() => handleDelete(row)}
                className="cursor-pointer text-red-600 text-lg"
              />
            </div>
          </div>
        ))
      )}
    </div>

    {/* FORM MODAL */}
    {formOpen && (
      <AddEditGroupMaster
        editingData={editingData}
        onClose={() => setFormOpen(false)}
        onSuccess={fetchGroups}
      />
    )}
  </div>
);
};

export default GroupMaster;